import React from 'react';
import { RefreshCw } from 'lucide-react';
import { BEACH_FLAGS } from '../../constants/weatherCodes';
import { suggestFlagBasedOnConditions } from '../../utils/weatherHelpers';

const BeachFlagModal = ({ 
  show, 
  onClose, 
  onUpdateFlag, 
  flagStatus, 
  weather 
}) => {
  if (!show) return null;

  const suggestion = suggestFlagBasedOnConditions(weather);

  const flagOptions = [
    { color: '🟢', text: 'Green - Low hazard', details: 'Calm conditions, light surf' },
    { color: '🟡', text: 'Yellow - Medium hazard', details: 'Moderate surf and currents' },
    { color: '🔴', text: 'Red - High hazard', details: 'Strong surf and currents' },
    { color: '🔴🔴', text: 'Double Red - Water closed', details: 'Water closed to public' },
    { color: '🟣', text: 'Purple - Marine pests', details: 'Dangerous marine life' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 animate-slide-down max-h-[90vh] overflow-y-auto">
        <h3 id="modal-title" className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">🚩 Update Beach Flag Status</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Check the current beach conditions from official sources or observe the physical flags at the beach.
        </p>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Check the current beach conditions from these sources:</p>
          
          <div className="space-y-2">
            <a 
              href="https://www.facebook.com/sgcfire/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              South Gulf Fire Rescue Facebook
            </a>
            
            <a 
              href="https://www.visitgulf.com/things-to-do/beaches/beach-safety/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full bg-orange-600 text-white text-center py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              Gulf County Beach Safety Page
            </a>
            
            <div className="text-xs text-gray-600 dark:text-gray-400 text-center mt-2">
              <p>Physical flags are posted at:</p>
              <p>• North & South fire stations on Cape San Blas</p>
              <p>• Barrier Dunes primary walk-over</p>
            </div>
          </div>
        </div>

        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Select the current flag:</p>
        
        {suggestion && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <span className="font-medium">Weather suggests:</span> {suggestion.suggested}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
              {suggestion.reason}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              Always verify with official sources above
            </p>
          </div>
        )}
        
        <div className="space-y-2 mb-4">
          {flagOptions.map((option) => (
            <button
              key={option.color}
              onClick={() => onUpdateFlag(option.color, option.text, option.details)}
              className="w-full text-left p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
              type="button"
            >
              <span className="text-2xl">{option.color}</span>
              <div>
                <p className="font-medium dark:text-gray-100">{option.text.split(' - ')[1]}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">{option.details}</p>
              </div>
            </button>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full text-center text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-200"
          type="button"
        >
          Skip for now
        </button>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3">
          Last updated: {flagStatus.lastUpdated}
        </p>
      </div>
    </div>
  );
};

export default BeachFlagModal;